import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MoveDownOutlinedIcon from '@mui/icons-material/MoveDownOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  downloadUnusedPickingStockReport,
  getUnusedPickingStockReport,
} from '../../../../api/mix-deliveriesApi';

import { useCallback, useEffect, useMemo, useState } from 'react';

const formatDateKey = (value) => {
  if (!value) {
    return '—';
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));

  if (!match) {
    return String(value);
  }

  return `${match[3]}.${match[2]}.${match[1]}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',

    year: 'numeric',
    month: '2-digit',
    day: '2-digit',

    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatQuantity = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(numericValue);
};

const getErrorMessage = (error) => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(' ');
  }

  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Wystąpił nieznany błąd.';
};

const SummaryCard = ({ title, value, description, icon }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            width: 44,
            height: 44,

            borderRadius: 2,
            bgcolor: 'action.hover',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              mt: 0.25,
            }}
          >
            {value}
          </Typography>

          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const QuantitySummary = ({ values }) => {
  if (!Array.isArray(values) || values.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Brak ilości do podsumowania.
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {values.map((item) => (
        <Chip
          key={`${item.unit}-${item.quantity}`}
          variant="outlined"
          label={`${formatQuantity(item.quantity)} ${item.unit || ''}`}
        />
      ))}
    </Stack>
  );
};

const MixUnusedPickingStockReport = () => {
  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(true);

  const [downloading, setDownloading] = useState(false);

  const [error, setError] = useState('');

  const [downloadError, setDownloadError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getUnusedPickingStockReport();

      setReport(result);
    } catch (requestError) {
      setReport(null);
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError('');

    try {
      await downloadUnusedPickingStockReport();
    } catch (requestError) {
      setDownloadError(getErrorMessage(requestError));
    } finally {
      setDownloading(false);
    }
  };

  const rows = useMemo(
    () => (Array.isArray(report?.data) ? report.data : []),
    [report],
  );

  const warnings = useMemo(
    () =>
      Array.isArray(report?.freshness?.warnings)
        ? report.freshness.warnings
        : [],
    [report],
  );

  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          minHeight={240}
        >
          <CircularProgress />

          <Typography color="text.secondary">
            Pobieranie raportu nieużywanego zapasu...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>

        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadReport}
          >
            Spróbuj ponownie
          </Button>
        </Box>
      </Stack>
    );
  }

  if (!report) {
    return (
      <Alert severity="info">Nie znaleziono wygenerowanego raportu.</Alert>
    );
  }

  const isCurrent = report?.freshness?.isCurrent === true;

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'flex-start',
          }}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="h5" fontWeight={700}>
                Materiały do przeniesienia z 0D20 do 0D30
              </Typography>

              <Chip
                size="small"
                color={isCurrent ? 'success' : 'warning'}
                label={isCurrent ? 'Raport aktualny' : 'Raport nieaktualny'}
              />
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
              }}
            >
              Raport zawiera produkty znajdujące się w 0D20, które nie występują
              w żadnej aktywnej pozycji miksowej w całym analizowanym zakresie
              dostaw.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadReport}
              disabled={loading}
            >
              Odśwież
            </Button>

            <Button
              variant="contained"
              startIcon={
                downloading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownload}
              disabled={downloading || !isCurrent}
            >
              {downloading ? 'Generowanie...' : 'Pobierz Excel'}
            </Button>
          </Stack>
        </Stack>

        <Divider
          sx={{
            my: 2.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Zakres analizy
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {formatDateKey(report.dateFrom)} – {formatDateKey(report.dateTo)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Wygenerowano
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {formatDateTime(report.generatedAt)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Snapshot zapasu
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {report.sources?.stockSnapshot?.fileName || '—'}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {formatDateTime(report.sources?.stockSnapshot?.createdAt)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Analiza dostaw
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {formatDateTime(report.sources?.mixImport?.analyzedAt)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {!isCurrent ? (
        <Alert severity="warning" icon={<WarningAmberOutlinedIcon />}>
          <Typography variant="body2" fontWeight={600}>
            Raport nie korzysta z najnowszych danych.
          </Typography>

          {warnings.length > 0 ? (
            <Box
              component="ul"
              sx={{
                mt: 1,
                mb: 0,
                pl: 2.5,
              }}
            >
              {warnings.map((warning) => (
                <li key={warning}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </Box>
          ) : null}

          <Typography
            variant="body2"
            sx={{
              mt: 1,
            }}
          >
            Pobieranie Excela jest zablokowane do czasu ponownego wygenerowania
            raportu.
          </Typography>
        </Alert>
      ) : null}

      {downloadError ? (
        <Alert severity="error" onClose={() => setDownloadError('')}>
          {downloadError}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <SummaryCard
          title="Materiały w 0D20"
          value={report.summary?.pickingAreaMaterials ?? 0}
          description="Unikalne materiały w strefie"
          icon={<Inventory2OutlinedIcon />}
        />

        <SummaryCard
          title="Materiały wykorzystywane"
          value={report.summary?.materialsUsedInRange ?? 0}
          description="Używane w aktualnym zakresie"
          icon={<Inventory2OutlinedIcon />}
        />

        <SummaryCard
          title="Materiały nieużywane"
          value={report.summary?.unusedMaterials ?? 0}
          description="Kandydaci do przeniesienia"
          icon={<MoveDownOutlinedIcon />}
        />

        <SummaryCard
          title="Lokalizacje źródłowe"
          value={report.summary?.sourceBins ?? 0}
          description={`Pozycje zapasu: ${
            report.summary?.stockRowsToMove ?? 0
          }`}
          icon={<LocationOnOutlinedIcon />}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Łączna ilość do przeniesienia
        </Typography>

        <QuantitySummary values={report.summary?.quantityToMoveByUnit} />
      </Paper>

      <Paper variant="outlined">
        <Box
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Pozycje zapasu
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Każdy wiersz reprezentuje konkretną lokalizację źródłową w strefie
            0D20.
          </Typography>
        </Box>

        <Divider />

        {rows.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography fontWeight={600}>
              Brak materiałów do przeniesienia.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Wszystkie materiały z 0D20 występują w aktualnie analizowanych
              dostawach miksowych.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: 620,
            }}
          >
            <Table
              stickyHeader
              size="small"
              aria-label="Materiały do przeniesienia z 0D20 do 0D30"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Lp.</TableCell>

                  <TableCell>Materiał</TableCell>

                  <TableCell>Nazwa produktu</TableCell>

                  <TableCell>Partia</TableCell>

                  <TableCell>Typ zapasu</TableCell>

                  <TableCell>Bin źródłowy</TableCell>

                  <TableCell align="right">Ilość</TableCell>

                  <TableCell>JM</TableCell>

                  <TableCell>Cel</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    hover
                    key={[
                      row.sourceRowNumber,
                      row.materialNumber,
                      row.sourceStorageBin,
                      row.batch,
                      index,
                    ].join('-')}
                  >
                    <TableCell>{index + 1}</TableCell>

                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}
                    >
                      {row.materialNumber}
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 240,
                      }}
                    >
                      {row.description || '—'}
                    </TableCell>

                    <TableCell>{row.batch || '—'}</TableCell>

                    <TableCell>{row.stockType || '—'}</TableCell>

                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.sourceStorageBin || '—'}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatQuantity(row.quantity)}
                    </TableCell>

                    <TableCell>{row.unit || '—'}</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.targetStorageType || '0D30'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};

export default MixUnusedPickingStockReport;
